/**
 * FR-017 — React Query hooks over the typed link table.
 *
 * Here rather than inline in the components so the object-view suites can
 * mock them the way they mock every `useTwin` hook (they render bare, with
 * no QueryClientProvider).
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { refreshLinkQueries, targetLinksKey } from '../components/twinLinks/linkQueries';
import { twinService, type DocumentLink } from '../services/twinService';

/** This twin item's live link rows (each with its `link_id`). */
export function useTargetLinks(targetType: string, targetId: string, enabled = true) {
    return useQuery<DocumentLink[]>({
        queryKey: targetLinksKey(targetType, targetId),
        queryFn: () => twinService.getTargetLinks(targetType, targetId),
        enabled,
    });
}

/** Supersede one link, then refresh every surface that reads links. */
export function useSupersedeLink() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (linkId: string) => twinService.supersedeLink(linkId),
        onSuccess: () => refreshLinkQueries(queryClient),
    });
}

/** A callback that refreshes every surface that reads links. */
export function useRefreshLinkQueries(): () => void {
    const queryClient = useQueryClient();
    return useCallback(() => refreshLinkQueries(queryClient), [queryClient]);
}
